import {apiFetch} from "./client";

export interface SellAddress {
    id: string;
    merchantId: string;
    network: "trc20";
    address: string;
    ownerTraderId: string | null;
    ownerTeamId: string | null;
    ownerName: string | null;
    clientRef: string | null;
    active: boolean;
    disabledAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface SellAddressListResponse {items: SellAddress[];}
export interface CreateSellAddressRequest {network: "trc20"; address: string; ownerTraderId: string;}

export function listSellAddresses(token: string | null, merchantId: string): Promise<SellAddressListResponse> {
    return apiFetch<SellAddressListResponse>(`/merchant/${encodeURIComponent(merchantId)}/exchange/sell-addresses`, token);
}

export function createSellAddress(token: string | null, merchantId: string, body: CreateSellAddressRequest): Promise<SellAddress> {
    return apiFetch<SellAddress>(`/merchant/${encodeURIComponent(merchantId)}/exchange/sell-addresses`, token, {
        method: "POST", body: JSON.stringify(body),
    });
}

export function updateSellAddress(token: string | null, merchantId: string, addressId: string, active: boolean): Promise<SellAddress> {
    return apiFetch<SellAddress>(`/merchant/${encodeURIComponent(merchantId)}/exchange/sell-addresses/${encodeURIComponent(addressId)}`, token, {
        method: "PATCH", body: JSON.stringify({active}),
    });
}

export interface ExchangeSellDeposit {
    status: string;
    address: string | null;
    network: string | null;
    expectedAmount: string | null;
    receivedAmount: string | null;
    txHash: string | null;
    receiptOk: boolean;
    publishedAt: string | null;
    deadline: string | null;
    expiredAt: string | null;
    confirmedAt: string | null;
}

export interface ExchangeSellPanelState {
    direction: "sell";
    clientRef: string | null;
    rateQuoteId: string | null;
    rate: string | null;
    clientMarkedPaidAt: string | null;
    deposit: ExchangeSellDeposit | null;
    reviewRequired: boolean;
    reviewBlocking: boolean;
    reviewReason: string | null;
    canCompleteRub: boolean;
    terms?: ExchangeSellTerms;
}

export interface ExchangeSellTerms {
    revision: number;
    originalUsdtAmount: string;
    originalRubKop: string;
    usdtAmount: string;
    rubKop: string;
    adjustedAt: string | null;
}

export function publishExchangeSellAddress(paymentId: string, token: string | null): Promise<{exchange?: ExchangeSellPanelState}> {
    return apiFetch<{exchange?: ExchangeSellPanelState}>(`/payments/${encodeURIComponent(paymentId)}/exchange/address/publish`, token, {method: "POST"});
}

export interface ExchangeSellReviewHistoryItem {
    commandId: string;
    action: "adjust" | "unblock" | string;
    actor: string;
    reason: string;
    priorUsdtAmount: string;
    newUsdtAmount: string;
    priorRubKop: string;
    newRubKop: string;
    revision: number;
    createdAt: string;
}

export interface ExchangeSellReviewResponse {
    orderId: string;
    exchange: ExchangeSellPanelState;
    commands: ExchangeSellReviewHistoryItem[];
}

export interface ExchangeSellReviewCommand {
    commandId: string;
    expectedRevision: number;
    reason: string;
}

export interface ExchangeSellAdjustCommand extends ExchangeSellReviewCommand {
    amount: string;
    /** Optional explicit RUB amount, with at most two decimal places. */
    rubAmount?: string;
}

/** Parse a positive decimal amount into USDT atomic units without using floats. */
export function usdtAmountToAtomic(raw: string): bigint | null {
    const normalized = raw.trim().replace(/\s/g, "").replace(",", ".");
    const match = /^(\d+)(?:\.(\d{1,6}))?$/.exec(normalized);
    if (!match) return null;
    const fraction = (match[2] ?? "").padEnd(6, "0");
    const atomic = BigInt(match[1]) * 1_000_000n + BigInt(fraction || "0");
    return atomic > 0n ? atomic : null;
}

/** Preview server's floor(USDT * frozen rate) settlement using integer arithmetic. */
export function previewRubKopecks(usdtAmount: string, frozenRate: string): bigint | null {
    const usdt = usdtAmountToAtomic(usdtAmount);
    const normalizedRate = frozenRate.trim().replace(/\s/g, "").replace(",", ".");
    const rateMatch = /^(\d+)(?:\.(\d{1,6}))?$/.exec(normalizedRate);
    if (!usdt || !rateMatch) return null;
    const rateDecimals = (rateMatch[2] ?? "").length;
    const rateScale = 10n ** BigInt(rateDecimals);
    const rate = BigInt(rateMatch[1]) * rateScale + BigInt(rateMatch[2] ?? "0");
    if (rate <= 0n) return null;
    return (usdt * rate * 100n) / (1_000_000n * rateScale);
}

export function formatRubKopecks(raw: string | null | undefined): string {
    if (raw == null || !/^\d+$/.test(raw)) return "—";
    const digits = raw.padStart(3, "0");
    const whole = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${whole},${digits.slice(-2)} ₽`;
}

export function getExchangeSellReview(token: string | null, orderId: string): Promise<ExchangeSellReviewResponse> {
    return apiFetch<ExchangeSellReviewResponse>(`/orders/${encodeURIComponent(orderId)}/exchange/sell/review`, token);
}

export function adjustExchangeSell(token: string | null, orderId: string, body: ExchangeSellAdjustCommand): Promise<{exchange: ExchangeSellPanelState}> {
    return apiFetch<{exchange: ExchangeSellPanelState}>(`/orders/${encodeURIComponent(orderId)}/exchange/sell/adjust`, token, {
        method: "POST", body: JSON.stringify(body),
    });
}

export function unblockExchangeSell(token: string | null, orderId: string, body: ExchangeSellReviewCommand): Promise<{exchange: ExchangeSellPanelState}> {
    return apiFetch<{exchange: ExchangeSellPanelState}>(`/orders/${encodeURIComponent(orderId)}/exchange/sell/unblock`, token, {
        method: "POST", body: JSON.stringify(body),
    });
}

export interface DepositCase {
    id: string;
    orderId: string | null;
    merchantId: string | null;
    addressId: string | null;
    address: string | null;
    network: string | null;
    tokenContract: string | null;
    clientRef: string | null;
    eventIndex: number | null;
    blockTime: string | null;
    expectedAmount: string | null;
    receivedAmount: string | null;
    reason: string;
    blocking: boolean;
    status: "open" | "resolved" | string;
    txHash: string | null;
    receiptOk: boolean;
    confirmedAt: string | null;
    assignedTo: string | null;
    assignedToName: string | null;
    owner: string | null;
    ownerTraderId: string | null;
    ownerTraderName: string | null;
    ownerTeamId: string | null;
    ownerTeamName: string | null;
    merchantName: string | null;
    createdAt: string;
    updatedAt: string;
}
export interface DepositCaseComment {authorName?: string | null; id: string; text: string; author: string; createdAt: string;}
export interface DepositCaseDetails extends DepositCase {comments: DepositCaseComment[]; history: Array<Record<string, unknown>>;}
export interface DepositCaseListResponse {items: DepositCase[]; limit: number; offset: number; hasMore: boolean;}
export interface DepositCaseAssignee {id: string; name: string;}

export function listDepositCases(token: string | null, query = ""): Promise<DepositCaseListResponse> {
    return apiFetch<DepositCaseListResponse>(`/exchange/deposit-cases${query}`, token);
}
export function listDepositCaseAssignees(token: string | null): Promise<{items: DepositCaseAssignee[]}> {
    return apiFetch<{items: DepositCaseAssignee[]}>("/exchange/deposit-cases/assignees", token);
}
export function getDepositCase(token: string | null, caseId: string): Promise<DepositCaseDetails> {
    return apiFetch<DepositCaseDetails>(`/exchange/deposit-cases/${encodeURIComponent(caseId)}`, token);
}
export function assignDepositCase(token: string | null, caseId: string, assigneeId: string): Promise<DepositCaseDetails> {
    return apiFetch<DepositCaseDetails>(`/exchange/deposit-cases/${encodeURIComponent(caseId)}/assign`, token, {method: "POST", body: JSON.stringify({assigneeId})});
}
export function commentDepositCase(token: string | null, caseId: string, text: string): Promise<DepositCaseComment> {
    return apiFetch<DepositCaseComment>(`/exchange/deposit-cases/${encodeURIComponent(caseId)}/comments`, token, {method: "POST", body: JSON.stringify({text})});
}
export function attachDepositCase(token: string | null, caseId: string, orderId: string): Promise<DepositCaseDetails> {
    return apiFetch<DepositCaseDetails>(`/exchange/deposit-cases/${encodeURIComponent(caseId)}/attach`, token, {method: "POST", body: JSON.stringify({orderId})});
}

export function openExchangeSellDispute(paymentId: string, token: string | null, reason: string): Promise<unknown> {
    return apiFetch(`/payments/${encodeURIComponent(paymentId)}/exchange/dispute`, token, {method: "POST", body: JSON.stringify({reason})});
}

export function closeExchangeSell(token: string | null, orderId: string, body: {reason: string}): Promise<unknown> {
    return apiFetch(`/orders/${encodeURIComponent(orderId)}/exchange/sell/close`, token, {method: "POST", body: JSON.stringify(body)});
}
